import { useEffect, useCallback, useMemo } from 'react';
import { FeatureFlagName } from 'shared/enums';
import { useAuth } from 'shared/providers/AuthProvider';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import { useAppSelector } from 'state/hooks';
import useClinicUser from './useClinicUser';
import useFeatureFlag from './useFeatureFlag';
import { useModularity } from './useModularity';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    pendo?: any;
  }
}

const usePendo = (): void => {
  const { isAuthenticated } = useAuth();
  const { isImpersonating, impersonatingUser } = useAppSelector((state) => state.currentClinic);
  const { clinicUser, currentClinic, hasAdminRoleAtCurrentClinic, isLoadingUserData, currentClinicActiveSubscription } =
    useClinicUser();
  const { isRecapClinic } = useModularity();
  const { isFeatureEnabled, isFeatureFlagsLoading, featureFlags } = useFeatureFlag();
  const { hasActiveBitwerxIntegration, allActiveIntegrations, primaryIntegrationName } = useIntegrationsProvider();
  const pendo = window.pendo;

  const activeIntegrationNames = useMemo(() => {
    return allActiveIntegrations.map((integration) => integration.type);
  }, [allActiveIntegrations]);

  const initializePendo = useCallback(() => {
    if (!clinicUser || !currentClinic || !pendo || isLoadingUserData) return;
    const currentUser = isImpersonating && impersonatingUser ? impersonatingUser : clinicUser;

    pendo.initialize({
      visitor: {
        id: currentUser.email,
        email: currentUser.email,
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
        role: currentUser.isAdmin || hasAdminRoleAtCurrentClinic ? 'Admin' : 'User',
        isImpersonating,
      },
      account: {
        id: isRecapClinic || !currentClinic.crmId ? currentClinic.id : currentClinic.crmId,
        accountName: currentClinic.name,
        stripeChargesEnabled: !!currentClinic.submerchantStripeChargesEnabled || false,
        stripePayoutsEnabled: !!currentClinic.submerchantStripePayoutsEnabled || false,
        stripeDefaultAccount: !!currentClinic.submerchantStripePayoutsEnabled || false, // No need to query default account since clinics can not have payouts enabled without a default account
        isBitwerxIntegratedAndActive: hasActiveBitwerxIntegration,
        // TODO: These are temporary fields that should be removed once we have configured our SalesForce integration
        enabledFeatureFlags: Object.keys(featureFlags),
        allActiveIntegrations: activeIntegrationNames,
        primaryIntegrationSystemName: primaryIntegrationName,
        productPackageName: currentClinicActiveSubscription?.productPackage?.name,
        isRecapClinic,
      },
    });
  }, [
    clinicUser,
    currentClinic,
    pendo,
    isLoadingUserData,
    isImpersonating,
    impersonatingUser,
    hasAdminRoleAtCurrentClinic,
    hasActiveBitwerxIntegration,
    featureFlags,
    activeIntegrationNames,
    primaryIntegrationName,
    currentClinicActiveSubscription,
    isRecapClinic,
  ]);

  // Initialize Pendo when the user is authenticated, feature flags are loaded, and the Pendo feature flag is enabled
  useEffect(() => {
    if (isFeatureFlagsLoading || !isFeatureEnabled(FeatureFlagName.EnablePendo) || !isAuthenticated) return;

    initializePendo();
  }, [isAuthenticated, initializePendo, isFeatureEnabled, isFeatureFlagsLoading]);
};

export default usePendo;
